import React, { useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import {
    FaArrowRight,
    FaBuilding,
    FaCheckCircle,
    FaChartLine,
    FaCreditCard,
    FaExchangeAlt,
    FaGlobe,
    FaShieldAlt,
    FaSpinner,
    FaStar,
    FaTimesCircle,
    FaUniversity,
} from 'react-icons/fa';
import { BUSINESS_PLUS_COMPARISON_DATA } from '../../BusinessLiteForms/constants';
import { V2_TYPE, V2_TYPE_LABELS } from '../helperV2';
import './PlanSwitchButton.scss';

/* ════════════════════════════════════════════════════════════════════════
   Plan copy — describes each account tier for the modal
   ════════════════════════════════════════════════════════════════════════ */
const PLAN_COPY = {
    [V2_TYPE.PLUS]: {
        eyebrow: 'Operational banking',
        summary:
            'Day-to-day banking, cards, global payments and treasury workflows — designed for operational businesses.',
        audience:
            'Best for agencies, consulting firms, technology companies, e-commerce businesses, holding companies, proprietary trading firms and family offices.',
        highlights: [
            { icon: FaGlobe, label: 'Global USD payments' },
            { icon: FaCreditCard, label: 'Team cards & spend control' },
            { icon: FaShieldAlt, label: 'Banking + custody' },
        ],
    },
    [V2_TYPE.PRIME]: {
        eyebrow: 'Higher-limit & regulated activity',
        summary:
            'Higher-volume workflows, client fund use cases, settlement activity and licensed financial-service businesses.',
        audience:
            'Best for NBFIs- MSBs, VASPs, funds, exchanges, OTC desks, remittance, FX, brokerage, liquidity providers and similar regulated businesses.',
        highlights: [
            { icon: FaUniversity, label: 'Institutional account' },
            { icon: FaExchangeAlt, label: 'Settlement workflows' },
            { icon: FaChartLine, label: 'Higher-limit path' },
        ],
    },
};

/* Pre-computed comparison data */
const SHARED_CAPABILITIES = BUSINESS_PLUS_COMPARISON_DATA
    .filter((item) => item?.bPlus?.isValid === true && item?.bPrime?.isValid === true)
    .slice(0, 8)
    .map((item) => item.title);

const PRIME_EXCLUSIVE = BUSINESS_PLUS_COMPARISON_DATA
    .filter((item) => item?.bPlus?.isValid === false && item?.bPrime?.isValid === true)
    .map((item) => ({
        title: item.title,
        description: item?.bPrime?.description || '',
    }));

const ONBOARDING_TIME = BUSINESS_PLUS_COMPARISON_DATA.find(
    (item) => item?.title === 'Onboarding Time',
);

const COMPARISON_MATRIX_ROWS = BUSINESS_PLUS_COMPARISON_DATA.filter(
    (item) => item?.title !== 'Onboarding Time',
);

/* ════════════════════════════════════════════════════════════════════════
   PlanUpgradeModal — premium comparison & switch modal
   ════════════════════════════════════════════════════════════════════════ */
const PlanUpgradeModal = ({
    isOpen,
    onClose,
    currentType,
    onSwitchType,
    disabled,
    loading = false,
}) => {
    const isCurrentlyPlus = Number(currentType) === V2_TYPE.PLUS;
    const defaultTarget = isCurrentlyPlus ? V2_TYPE.PRIME : Number(currentType);
    const [selectedType, setSelectedType] = useState(defaultTarget);

    // Reset selection when modal opens
    React.useEffect(() => {
        if (isOpen) {
            setSelectedType(isCurrentlyPlus ? V2_TYPE.PRIME : Number(currentType));
        }
    }, [currentType, isCurrentlyPlus, isOpen]);

    const isSelectionSameAsCurrent = selectedType === Number(currentType);

    const primaryLabel = isSelectionSameAsCurrent
        ? `Continue with ${V2_TYPE_LABELS[currentType]}`
        : `Switch to ${V2_TYPE_LABELS[selectedType]}`;

    const handlePrimary = async () => {
        if (loading) {
            return;
        }
        if (isSelectionSameAsCurrent) {
            onClose();
            return;
        }
        const result = await onSwitchType?.(selectedType);
        if (result !== false) onClose();
    };

    const onboardingSummary = useMemo(() => {
        if (!ONBOARDING_TIME) return null;
        return {
            plus: ONBOARDING_TIME?.bPlus?.description,
            prime: ONBOARDING_TIME?.bPrime?.description,
        };
    }, []);

    const renderComparisonCell = (value, planClassName) => {
        if (value?.isValid === true) {
            return (
                <div className={`plan-upgrade-modal__matrix-value is-valid ${planClassName}`}>
                    <FaCheckCircle size={13} />
                    <span>{value?.description || 'Included'}</span>
                </div>
            );
        }

        if (value?.isValid === false) {
            return (
                <div className={`plan-upgrade-modal__matrix-value is-invalid ${planClassName}`}>
                    <FaTimesCircle size={13} />
                    <span>{value?.description || 'Not included'}</span>
                </div>
            );
        }

        return (
            <div className={`plan-upgrade-modal__matrix-value is-neutral ${planClassName}`}>
                <span>{value?.description || 'Varies'}</span>
            </div>
        );
    };

    return (
        <Modal
            show={isOpen}
            onHide={onClose}
            centered
            size='xl'
            dialogClassName='plan-upgrade-modal__dialog'
            contentClassName='plan-upgrade-modal__chrome'
        >
            <div className='plan-upgrade-modal__panel'>
                <button
                    type='button'
                    className='plan-upgrade-modal__close'
                    onClick={onClose}
                    aria-label='Close'
                    disabled={loading}
                >
                    ×
                </button>

                {/* ── Hero Banner ──────────────────────────────────── */}
                <div className='plan-upgrade-modal__hero'>
                    <h3>
                        {isCurrentlyPlus
                            ? 'Unlock more with Business Prime'
                            : 'Choose the account that matches your needs'}
                    </h3>
                    <p>
                        {isCurrentlyPlus
                            ? 'Business Prime unlocks institutional-grade features, higher limits, settlement workflows, and API access — built for businesses ready to scale.'
                            : 'FV Bank Business Plus covers day-to-day banking. Business Prime adds higher-volume, settlement, and regulated use-case support.'}
                    </p>
                </div>

                {/* ── Content Area ─────────────────────────────────── */}
                <div className='plan-upgrade-modal__content'>

                    {/* Plan Cards */}
                    <div className='plan-upgrade-modal__cards'>
                        {[V2_TYPE.PLUS, V2_TYPE.PRIME].map((type) => {
                            const copy = PLAN_COPY[type];
                            const isCurrent = Number(currentType) === type;
                            const isSelected = selectedType === type;
                            const isPrime = type === V2_TYPE.PRIME;
                            const shouldRecommend = isPrime && isCurrentlyPlus;

                            return (
                                <button
                                    key={type}
                                    type='button'
                                    className={[
                                        'plan-upgrade-modal__card',
                                        isPrime ? 'is-prime' : '',
                                        isSelected ? 'is-selected' : '',
                                        shouldRecommend ? 'is-recommended' : '',
                                    ].filter(Boolean).join(' ')}
                                    onClick={() => setSelectedType(type)}
                                    disabled={loading}
                                >
                                    <div className='plan-upgrade-modal__card-header'>
                                        <span className={`plan-upgrade-modal__plan-badge ${isPrime ? 'is-prime' : 'is-plus'}`}>
                                            {V2_TYPE_LABELS[type]}
                                        </span>
                                        {isCurrent && (
                                            <span className='plan-upgrade-modal__current-tag'>
                                                <span className='plan-upgrade-modal__current-tag-dot' />
                                                Current
                                            </span>
                                        )}
                                    </div>
                                    {/* <h4>{copy.eyebrow}</h4>  */}
                                    <p className='plan-upgrade-modal__card-desc'>{copy.audience}</p>
                                    <div className='plan-upgrade-modal__card-features'>
                                        {copy.highlights.map(({ icon: Icon, label }) => (
                                            <span key={label} className='plan-upgrade-modal__feature-chip'>
                                                <Icon size={11} />
                                                {label}
                                            </span>
                                        ))}
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    {/* Shared Capabilities */}
                    <div className='plan-upgrade-modal__comparison'>
                        <div className='plan-upgrade-modal__comparison-title'>
                            <span className='plan-upgrade-modal__comparison-title-icon'>
                                <FaCheckCircle size={12} />
                            </span>
                            Compare Business Plus vs Business Prime
                        </div>
                        <div className='plan-upgrade-modal__matrix'>
                            <div className='plan-upgrade-modal__matrix-header plan-upgrade-modal__matrix-header--feature'>
                                Capability
                            </div>
                            <div className='plan-upgrade-modal__matrix-header'>Business Plus</div>
                            <div className='plan-upgrade-modal__matrix-header'>Business Prime</div>

                            {COMPARISON_MATRIX_ROWS.map((item) => {
                                const isPrimeAdvantage = item?.bPlus?.isValid === false && item?.bPrime?.isValid === true;
                                return (
                                    <React.Fragment key={item.title}>
                                        <div className={`plan-upgrade-modal__matrix-feature ${isPrimeAdvantage ? 'is-prime-advantage' : ''}`}>
                                            {item.title}
                                        </div>
                                        <div className='plan-upgrade-modal__matrix-cell' data-plan='Business Plus'>
                                            {renderComparisonCell(item?.bPlus, 'is-plus')}
                                        </div>
                                        <div className='plan-upgrade-modal__matrix-cell' data-plan='Business Prime'>
                                            {renderComparisonCell(item?.bPrime, 'is-prime')}
                                        </div>
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className='plan-upgrade-modal__footer'>
                    <div className='plan-upgrade-modal__footer-note'>
                    </div>
                    <div className='plan-upgrade-modal__footer-actions'>
                        {!isCurrentlyPlus && isSelectionSameAsCurrent ? (
                            <button
                                type='button'
                                className='plan-upgrade-modal__btn-tertiary'
                                onClick={() => setSelectedType(V2_TYPE.PLUS)}
                                disabled={disabled || loading}
                            >
                                Switch to Business Plus
                            </button>
                        ) : null}
                        <button
                            type='button'
                            className={`plan-upgrade-modal__btn-primary ${isSelectionSameAsCurrent ? 'is-continue' : ''}`}
                            onClick={handlePrimary}
                            disabled={disabled || loading}
                        >
                            {loading ? (
                                <>
                                    <FaSpinner size={12} className='spinner' style={{ marginRight: 8 }} />
                                    Updating plan...
                                </>
                            ) : (
                                <>
                                    {primaryLabel}
                                    {!isSelectionSameAsCurrent && <FaArrowRight size={12} style={{ marginLeft: 6 }} />}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default PlanUpgradeModal;
